import React, { useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import type { RestrictedEditFormProps } from "./types";
import { authHeader } from "./wizard-helpers";

export default function RestrictedEditForm({
  system,
  sysAlias,
  onDone,
}: RestrictedEditFormProps) {
  const [name, setName] = useState(system.name);
  const [description, setDescription] = useState(system.description ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", sysAlias)}`;
      await axios.patch(url, { name, description }, { headers: authHeader() });
      onDone();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? ((err.response?.data as { message?: string })?.message ?? err.message)
        : "Failed to save.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }

  const lockedFields: { label: string; value: string | null }[] = [
    { label: "Cloud", value: system.cloudProvider },
    { label: "Infra type", value: system.infraType },
    { label: "Region", value: system.region },
    { label: "Template", value: system.templateSlug },
  ].filter((f) => f.value);

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Edit Stack
        </Typography>
        <Chip
          label={system.status}
          size="small"
          color="success"
          sx={{ textTransform: "capitalize" }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Name and description can be changed at any time.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </Paper>

      {/* Locked infrastructure fields */}
      {lockedFields.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Infrastructure (locked)
            </Typography>
            <Tooltip
              title="Cloud, region, and template cannot be changed after deployment. These settings are baked into your Kubernetes resources. To change them, create a new stack and migrate your data."
              placement="right"
              arrow
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <Stack spacing={1.5}>
            {lockedFields.map(({ label, value }) => (
              <Stack
                key={label}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: 90 }}
                >
                  {label}
                </Typography>
                <Chip
                  label={value}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace", opacity: 0.7 }}
                />
              </Stack>
            ))}
          </Stack>
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mt: 2, fontSize: "0.78rem" }}
          >
            To change cloud, region, or template — create a new stack and
            migrate your data across.
          </Alert>
        </Paper>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={handleSave}
          disabled={saving || !name.trim()}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="outlined" onClick={onDone} disabled={saving}>
          Cancel
        </Button>
      </Stack>
    </Container>
  );
}
